import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  useCreateConversationMutation,
  useLazyListMessagesQuery,
  useListConversationsQuery,
} from '@apis/aiCoreAPI'
import type {Conversation} from '@interfaces/alpinist/conversation'

/** Bounded retries for the initial force-new create (public embed, see below). */
const MAX_FORCE_NEW_CREATE_RETRIES = 5

type Options = {
  /** fetch conversation list at all */
  enabled?: boolean
  /** auto-pick latest / auto-create a conversation */
  bootstrap?: boolean
  /** initial conversation ID from URL (will be validated against available conversations) */
  initialConversationId?: string | null
  /**
   * Always create a brand-new conversation on mount, never adopt an existing one. Used by the
   * public demo embed: the demo tenant is shared across anonymous visitors and the list is
   * scoped by tenant only, so adopting "latest" could surface another visitor's conversation.
   */
  forceNew?: boolean
}

export function useActiveConversation(opts: Options = {}): {
  conversations: Conversation[]
  isLoading: boolean
  activeId: string | null
  setActiveId: (id: string | null) => void
  createNew: () => Promise<Conversation>
  /** A new conversation is being created (New conversation clicked). */
  creating: boolean
} {
  const enabled = opts.enabled ?? true
  const bootstrap = opts.bootstrap ?? true
  const initialConversationId = opts.initialConversationId
  const forceNew = opts.forceNew ?? false

  const initialIdRef = useRef(initialConversationId)
  // Tracks the last seen prop so the adopt-on-change effect below only reacts to
  // actual changes, leaving the mount case to the bootstrap effect.
  const prevInitialIdRef = useRef(initialConversationId)
  // Guards the async create so repeated effect runs (before activeId is set) can't create
  // more than one conversation.
  const creatingRef = useRef(false)

  const {data: conversations = [], isLoading} = useListConversationsQuery(
    // Include scheduled-report conversations alongside regular chats so they
    // show in the switcher (with an icon + type filter); incidents stay out.
    {category: 'general,scheduled'},
    {
      // In force-new (demo) mode we never list — the demo tenant is shared, so don't even
      // pull other visitors' conversations to the client.
      skip: !enabled || forceNew,
    },
  )
  const [createConversation, {isLoading: creating}] = useCreateConversationMutation()
  // Force-new resume validation only (cheap limit=1 probe; see bootstrap below).
  const [probeConversation] = useLazyListMessagesQuery()
  const [activeId, setActiveId] = useState<string | null>(null)
  // Bumping this re-runs the bootstrap effect → retries the force-new create.
  const [forceNewAttempt, setForceNewAttempt] = useState(0)

  // Adopt a conversation handed to an already-mounted chat, e.g. a widget's
  // "Start conversation" hand-off via openAlpinistPanel(conversationId). Only
  // reacts to prop CHANGES: the mount value is the bootstrap effect's job, and
  // activeId → onConversationChange → prop round-trips arrive equal to activeId.
  useEffect(() => {
    if (initialConversationId === prevInitialIdRef.current) {
      return
    }
    prevInitialIdRef.current = initialConversationId
    if (forceNew) {
      return
    }
    if (initialConversationId && initialConversationId !== activeId) {
      setActiveId(initialConversationId)
    }
  }, [initialConversationId, activeId, forceNew])

  const latest: Conversation | undefined = useMemo(() => {
    if (!conversations.length) {
      return undefined
    }
    return [...conversations].sort((a, b) => {
      const aKey = a.modified ?? a.created ?? ''
      const bKey = b.modified ?? b.created ?? ''
      return bKey.localeCompare(aKey)
    })[0]
  }, [conversations])

  // Bootstrap only when allowed
  useEffect(() => {
    if (!enabled || !bootstrap) {
      return
    }
    if (activeId) {
      return
    }

    // Demo embed: never wait for or adopt the (tenant-shared) list. Resume only the
    // conversation the caller explicitly hands us (the embed persists the id it created
    // for this visitor); otherwise start a fresh, isolated one.
    if (forceNew) {
      if (creatingRef.current) {
        return
      }
      creatingRef.current = true
      ;(async () => {
        try {
          const resumeId = initialIdRef.current
          if (resumeId) {
            // One attempt only — if the conversation is gone (deleted, retention), fall
            // through to a fresh create instead of re-probing on every effect run.
            initialIdRef.current = null
            try {
              await probeConversation({conversationId: resumeId, limit: 1}).unwrap()
              setActiveId(resumeId)
              return
            } catch {
              // fall through to a fresh conversation
            }
          }
          const c = await createConversation({title: '', category: 'general'}).unwrap()
          setActiveId(c.id)
        } catch {
          // The public embed has no navigation or reload affordance, so one transient
          // failure (cold start, blip) must not brick it on the spinner — retry with a
          // linear backoff, capped.
          if (forceNewAttempt < MAX_FORCE_NEW_CREATE_RETRIES) {
            setTimeout(() => setForceNewAttempt((a) => a + 1), 1500 * (forceNewAttempt + 1))
          }
        } finally {
          creatingRef.current = false
        }
      })()
      return
    }

    if (isLoading) {
      return
    }

    const initialId = initialIdRef.current
    if (initialId) {
      const exists = conversations.some((c) => c.id === initialId)
      if (exists) {
        setActiveId(initialId)
        return
      }
    }

    if (latest) {
      setActiveId(latest.id)
      return
    }

    if (creatingRef.current) {
      return
    }
    creatingRef.current = true
    ;(async () => {
      try {
        const c = await createConversation({title: '', category: 'general'}).unwrap()
        setActiveId(c.id)
      } finally {
        creatingRef.current = false
      }
    })()
  }, [
    enabled,
    bootstrap,
    activeId,
    isLoading,
    latest,
    createConversation,
    conversations,
    forceNew,
    forceNewAttempt,
    probeConversation,
  ])

  const createNew = useCallback(async () => {
    const c = await createConversation({title: '', category: 'general'}).unwrap()
    setActiveId(c.id)
    return c
  }, [createConversation])

  return {conversations, isLoading, activeId, setActiveId, createNew, creating}
}
